import React from 'react';
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material';
import { FONT } from '../Email/emailShared';

export { FONT, Progress, authToken, fmtDateTime, pct } from '../Email/emailShared';

export const WA_GREEN = '#25D366';

export const WA_STATUS = {
  draft: { label: 'Draft', bg: '#f1f5f9', fg: '#475569' },
  pending: { label: 'Starting', bg: '#fef3c7', fg: '#92400e' },
  scheduled: { label: 'Scheduled', bg: '#dbeafe', fg: '#1d4ed8' },
  running: { label: 'Sending', bg: '#dcfce7', fg: '#166534' },
  completed: { label: 'Completed', bg: '#d1fae5', fg: '#065f46' },
  cancelled: { label: 'Stopped', bg: '#fee2e2', fg: '#991b1b' },
};
export const WA_LOG_STATUS = {
  pending: { label: 'Waiting', bg: '#fef3c7', fg: '#92400e' },
  sent: { label: 'Sent', bg: '#dbeafe', fg: '#1d4ed8' },
  delivered: { label: 'Delivered', bg: '#dcfce7', fg: '#166534' },
  read: { label: 'Read', bg: '#d1fae5', fg: '#065f46' },
  failed: { label: 'Failed', bg: '#fee2e2', fg: '#991b1b' },
  cancelled: { label: 'Not sent', bg: '#f1f5f9', fg: '#475569' },
};
export const TEMPLATE_STATUS = {
  APPROVED: { label: 'Approved', bg: '#dcfce7', fg: '#166534' },
  PENDING: { label: 'In review', bg: '#fef3c7', fg: '#92400e' },
  REJECTED: { label: 'Rejected', bg: '#fee2e2', fg: '#991b1b' },
  PAUSED: { label: 'Paused', bg: '#ffedd5', fg: '#9a3412' },
  DISABLED: { label: 'Disabled', bg: '#f1f5f9', fg: '#475569' },
};

export const WaChip = ({ status, map = WA_STATUS }) => {
  const s = map[status] || { label: status || '-', bg: '#f1f5f9', fg: '#475569' };
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 24, background: s.bg, color: s.fg }} />;
};

// Customer fields a variable can be filled from when the audience comes from the database.
export const DB_FIELDS = ['name', 'mobile', 'email', 'city', 'state', 'gender', 'dob'];
export const DYN = /^\{\{dynamic:(.*)\}\}$/;
export const isDynamic = (v) => DYN.test(String(v || ''));
export const dynamicField = (v) => (String(v || '').match(DYN) || [])[1] || '';

// Fill {{1}} {{2}} in a template body with the entered values (a dynamic value shows as [field]).
export const fillWa = (text = '', values = {}) =>
  text.replace(/\{\{(\d+)\}\}/g, (all, n) => {
    const v = values[n];
    if (!v) return all;
    return isDynamic(v) ? `[${dynamicField(v)}]` : v;
  });

// A WhatsApp-style chat bubble: optional header, body, footer.
export const WaBubble = ({ header, headerType, headerImage, body, footer, empty = 'Your message appears here' }) => (
  <Box sx={{ background: '#e5ddd5', borderRadius: '18px', p: 2 }}>
    <Box sx={{ background: '#dcf8c6', borderRadius: '12px 12px 12px 2px', p: 1.25, boxShadow: '0 1px 2px rgba(15,23,42,.18)', position: 'relative', pb: 3 }}>
      {headerImage && (
        <Box sx={{ width: '100%', aspectRatio: '16 / 9', background: '#cbd5e1', borderRadius: '8px', mb: 1, backgroundImage: `url(${headerImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      )}
      {!headerImage && headerType && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) && (
        <Box sx={{ width: '100%', aspectRatio: '16 / 9', background: '#e2e8f0', borderRadius: '8px', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontFamily: FONT, fontSize: 11.5, fontWeight: 700 }}>
          {headerType} header
        </Box>
      )}
      {header && <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#111', mb: 0.5 }}>{header}</Typography>}
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: body ? '#111' : '#6b7280', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{body || empty}</Typography>
      {footer && <Typography sx={{ fontFamily: FONT, fontSize: 11, color: 'rgba(0,0,0,.5)', mt: 0.75 }}>{footer}</Typography>}
      <Typography sx={{ position: 'absolute', right: 10, bottom: 5, fontFamily: FONT, fontSize: 10.5, color: 'rgba(0,0,0,.45)' }}>10:42 am ✓✓</Typography>
    </Box>
  </Box>
);

// One row per template variable: a fixed text, or a value taken from each recipient's own data.
export const VariableEditor = ({ variables = [], values = {}, onChange, fields = DB_FIELDS }) => {
  const set = (name, v) => onChange({ ...values, [name]: v });
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      {variables.map((v) => {
        const dyn = isDynamic(values[v]);
        const all = dyn && !fields.includes(dynamicField(values[v])) ? [...fields, dynamicField(values[v])] : fields;
        return (
          <Box key={v} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 1.5, background: '#fff' }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 11.5, color: '#075e54', mb: 1 }}>{`{{${v}}}`}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField select size="small" value={dyn ? 'dynamic' : 'static'} onChange={(e) => set(v, e.target.value === 'dynamic' ? `{{dynamic:${all[0] || 'name'}}}` : '')} sx={{ width: 140 }}>
                <MenuItem value="static">Fixed text</MenuItem>
                <MenuItem value="dynamic">From customer</MenuItem>
              </TextField>
              {dyn ? (
                <TextField select size="small" fullWidth value={dynamicField(values[v])} onChange={(e) => set(v, `{{dynamic:${e.target.value}}}`)}>
                  {all.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </TextField>
              ) : (
                <TextField size="small" fullWidth placeholder="Value for everyone" value={values[v] || ''} onChange={(e) => set(v, e.target.value)} />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
